import { uniqueId } from 'common/utils/vanilla'
import { ArrayActionType } from './action'

export type ArrayAction<T = any> =
  | {
      type: ArrayActionType.REMOVE
      payload: string
    }
  | {
      type: ArrayActionType.REMOVE_BY_INDEX
      payload?: number
    }
  | {
      type: ArrayActionType.UPDATE
      payload: {
        id: string
        data: Partial<T> //  Use `Partial<T>` instead of `unknown`
      }
    }
  | {
      type: ArrayActionType.UPDATE_BY_INDEX
      payload: {
        index: number
        data: Partial<T> //  Use `Partial<T>` instead of `unknown`
      }
    }
  | {
      type: ArrayActionType.ADD
    }
  | {
      type: ArrayActionType.REPLACE_ALL
      payload: T[] //  Use `T[]` instead of `unknown[]`
    }

export type ArrayReducerRow<T> = {
  id: string
  data: T
}

export type IdConstructor = () => string
export const defaultIdConstructor: IdConstructor = () =>
  uniqueId('arrayReducer_')

const generateRow: (
  idConstructor: IdConstructor
) => <T>(data: T) => ArrayReducerRow<T> = getId => data => ({
  id: getId(),
  data,
})

const zeroValueIfEmpty =
  <T>(getZeroValue: () => ArrayReducerRow<T>) =>
  (state: ArrayReducerRow<T>[]) =>
    state.length === 0 ? [getZeroValue()] : state

export const arrayReducer =
  <T>(zeroValueRow: T, idConstructor: IdConstructor = defaultIdConstructor) =>
  (
    state: ArrayReducerRow<T>[] = [generateRow(idConstructor)(zeroValueRow)],
    action: ArrayAction<T>
  ) => {
    const getRowWithId = generateRow(idConstructor)
    const getZeroValueIfEmpty = zeroValueIfEmpty(() =>
      getRowWithId(zeroValueRow)
    )
    switch (action?.type) {
      case ArrayActionType.ADD: {
        return state.concat(getRowWithId(zeroValueRow))
      }
      case ArrayActionType.REPLACE_ALL: {
        const data = action.payload
        const rowsWithId = data.map(getRowWithId)
        return [...(rowsWithId as ArrayReducerRow<T>[])]
      }
      case ArrayActionType.REMOVE: {
        // if all values are removed, then fill with zero value
        const idToRemove = action.payload
        const newState = state.filter(({ id }) => id !== idToRemove)
        return getZeroValueIfEmpty(newState)
      }
      case ArrayActionType.REMOVE_BY_INDEX: {
        // if the index is not provided, remove the last item
        // if all values are removed, then fill with zero value
        const index = action.payload
        if (!index) {
          const newState = state.slice(0, state.length - 1)
          return getZeroValueIfEmpty(newState)
        }
        const newState = [...state]
        newState.splice(index, 1)
        return getZeroValueIfEmpty(newState)
      }
      case ArrayActionType.UPDATE: {
        const { id, data } = action.payload
        return state.map(item => {
          if (item.id === id) {
            return { ...item, data: { ...item.data, ...data } } //  Merge with existing data
          }
          return item
        })
      }

      case ArrayActionType.UPDATE_BY_INDEX: {
        const { index: indexToUpdate, data } = action.payload
        return state.map((item, index) =>
          index === indexToUpdate
            ? { ...item, data: { ...item.data, ...data } } //  Merge with existing data
            : item
        )
      }
      default:
        return state
    }
  }
